import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Autocomplete,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { MedicalRecord } from '../../utils/api';

interface VisitNoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    horseId: string;
    type: string;
    date: string;
    notes: string;
    tags: string[];
  }) => void;
  horseId: string;
  existingRecord?: MedicalRecord | null;
  tagSuggestions?: string[];
}

const VisitNoteDialog: React.FC<VisitNoteDialogProps> = ({
  open,
  onClose,
  onSave,
  horseId,
  existingRecord,
  tagSuggestions = [],
}) => {
  const [formData, setFormData] = useState({
    dateTime: new Date(),
    notes: '',
    tags: [] as string[],
  });

  useEffect(() => {
    if (existingRecord) {
      setFormData({
        dateTime: new Date(existingRecord.date),
        notes: existingRecord.notes || '',
        tags: existingRecord.tags || [],
      });
    } else {
      setFormData({
        dateTime: new Date(),
        notes: '',
        tags: [],
      });
    }
  }, [existingRecord, open]);

  const handleSubmit = () => {
    onSave({
      horseId,
      type: 'visit_note',
      date: formData.dateTime.toISOString(),
      notes: formData.notes,
      tags: formData.tags,
    });
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      dateTime: new Date(),
      notes: '',
      tags: [],
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {existingRecord ? 'Edit' : 'Add'} Visit Note
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Date & Time"
              value={formData.dateTime}
              onChange={(newValue) => {
                if (newValue) {
                  setFormData({ ...formData, dateTime: newValue });
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>

          <TextField
            label="Notes"
            multiline
            rows={8}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            fullWidth
            placeholder="Enter visit notes, observations, findings..."
            required
          />

          <Autocomplete
            multiple
            freeSolo
            options={tagSuggestions}
            value={formData.tags}
            onChange={(_, newValue) => setFormData({ ...formData, tags: newValue as string[] })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Type and press Enter to add tags"
                helperText="Optional — add tags to organize and filter notes"
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.notes.trim()}
        >
          {existingRecord ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitNoteDialog;
